import React, { useState, useEffect, useCallback } from 'react';
import { auditAPI } from '../../services/api';
import { formatDateTime } from '../../utils/dateFormat';
import './Admin.css';

interface AuditEntry {
  id: number;
  tenant_id: number | null;
  tenant_name: string | null;
  user_id: string | null;
  actor_name: string | null;
  actor_email: string | null;
  actor_role: string | null;
  action: string;
  resource_type: string | null;
  resource_id: number | null;
  details: Record<string, any> | null;
  description: string | null;
  ip_address: string | null;
  severity: 'info' | 'warning' | 'critical';
  event_category: string;
  created_at: string;
}

const SEVERITY_CONFIG: Record<string, { color: string; label: string }> = {
  info: { color: '#68d391', label: 'Info' },
  warning: { color: '#f6ad55', label: 'Warning' },
  critical: { color: '#fc8181', label: 'Critical' },
};

const CATEGORY_LABELS: Record<string, string> = {
  auth: 'Auth',
  data_access: 'Data Access',
  user_mgmt: 'User Management',
  user_management: 'User Management',
  admin: 'Admin',
  security: 'Security',
  import: 'Import',
  registration: 'Registration',
  sharing: 'Sharing',
  other: 'Other',
};

const AuditPage: React.FC = () => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const perPage = 50;

  const [severity, setSeverity] = useState('');
  const [category, setCategory] = useState('');
  const [dateRange, setDateRange] = useState('30days');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, per_page: perPage };
      if (severity) params.severity = severity;
      if (category) params.category = category;
      if (dateRange) params.date_range = dateRange;
      if (search) params.search = search;
      const res = await auditAPI.getLog(params);
      setEntries(res.data.entries);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (err) {
      console.error('Failed to load audit log:', err);
    } finally {
      setLoading(false);
    }
  }, [page, severity, category, dateRange, search]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleExportCSV = async () => {
    try {
      const params: any = {};
      if (severity) params.severity = severity;
      if (category) params.category = category;
      if (dateRange) params.date_range = dateRange;
      if (search) params.search = search;
      const res = await auditAPI.exportCSV(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_log_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export:', err);
    }
  };

  const formatAction = (action: string) =>
    action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const getRoleBadgeStyle = (role: string | null): React.CSSProperties => {
    if (role === 'admin' || role === 'super_admin') return { color: '#f87171', background: 'rgba(248, 113, 113, 0.15)' };
    if (role === 'viewer') return { color: '#718096', background: 'rgba(113, 128, 150, 0.15)' };
    return { color: '#68d391', background: 'rgba(104, 211, 145, 0.15)' };
  };

  const selectStyle: React.CSSProperties = {
    width: 'auto', minWidth: '130px',
    background: 'var(--content-bg)', border: '1px solid var(--card-border)',
    color: 'var(--text-primary)', borderRadius: '6px', padding: '7px 10px', fontSize: '13px',
  };

  return (
    <div className="admin-audit-page">
      {/* Filter Bar */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' }}>
        <select value={dateRange} onChange={(e) => { setDateRange(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Time</option>
          <option value="today">Today</option>
          <option value="7days">Last 7 Days</option>
          <option value="30days">Last 30 Days</option>
        </select>
        <select value={severity} onChange={(e) => { setSeverity(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Severity</option>
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="critical">Critical</option>
        </select>
        <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Categories</option>
          <option value="auth">Auth</option>
          <option value="data_access">Data Access</option>
          <option value="user_mgmt">User Management</option>
          <option value="admin">Admin</option>
          <option value="security">Security</option>
          <option value="import">Import</option>
          <option value="registration">Registration</option>
          <option value="sharing">Sharing</option>
        </select>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '6px', flex: 1, minWidth: '200px' }}>
          <div className="search-input-wrapper" style={{ flex: 1 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, email, or action..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </form>
        <button className="btn-secondary" onClick={handleExportCSV} style={{ whiteSpace: 'nowrap', fontSize: '13px', padding: '7px 14px' }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
            <path d="M8 2V10M4 7L8 11L12 7M3 14H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="admin-table-container">
        {loading ? (
          <div className="loading-state"><div className="admin-loading-spinner" /></div>
        ) : entries.length === 0 ? (
          <div className="empty-state">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <path d="M8 8H40M8 16H40M8 24H24M8 32H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <h3>No audit entries found</h3>
            <p>Try adjusting your filters</p>
          </div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: '36px' }}></th>
                  <th>Timestamp</th>
                  <th>Event</th>
                  <th>Actor</th>
                  <th>Tenant</th>
                  <th>Description</th>
                  <th style={{ width: '30px' }}></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const sev = SEVERITY_CONFIG[entry.severity] || SEVERITY_CONFIG.info;
                  const isCritical = entry.severity === 'critical';
                  const isExpanded = expandedId === entry.id;

                  return (
                    <React.Fragment key={entry.id}>
                      <tr
                        style={{ cursor: 'pointer', background: isCritical ? 'rgba(252, 129, 129, 0.04)' : undefined }}
                        onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                      >
                        <td>
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: sev.color }} title={sev.label} />
                        </td>
                        <td className="table-cell-secondary" style={{ whiteSpace: 'nowrap', fontSize: '12px' }}>
                          {formatDateTime(entry.created_at)}
                        </td>
                        <td>
                          <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{formatAction(entry.action)}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{CATEGORY_LABELS[entry.event_category] || entry.event_category}</div>
                        </td>
                        <td>
                          <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{entry.actor_name || 'System'}</div>
                          {entry.actor_role && (
                            <span style={{ ...getRoleBadgeStyle(entry.actor_role), fontSize: '10px', padding: '1px 6px', borderRadius: '4px' }}>
                              {entry.actor_role === 'user' ? 'member' : entry.actor_role}
                            </span>
                          )}
                        </td>
                        <td className="table-cell-secondary" style={{ fontSize: '12px' }}>{entry.tenant_name || '-'}</td>
                        <td className="table-cell-secondary" style={{ fontSize: '12px', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {entry.description || (entry.resource_type ? `${entry.resource_type}${entry.resource_id ? ` #${entry.resource_id}` : ''}` : '-')}
                        </td>
                        <td>
                          <svg style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', color: 'var(--text-secondary)' }} width="12" height="12" viewBox="0 0 16 16" fill="none">
                            <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={7} style={{ padding: '12px 16px', background: 'var(--hover-bg)', borderTop: 'none' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', fontSize: '12px' }}>
                              <div>
                                <div style={{ color: 'var(--text-secondary)', marginBottom: '2px' }}>Actor Email</div>
                                <div style={{ color: 'var(--text-primary)' }}>{entry.actor_email || '-'}</div>
                              </div>
                              <div>
                                <div style={{ color: 'var(--text-secondary)', marginBottom: '2px' }}>IP Address</div>
                                <div style={{ color: 'var(--text-primary)' }}>{entry.ip_address || '-'}</div>
                              </div>
                              <div>
                                <div style={{ color: 'var(--text-secondary)', marginBottom: '2px' }}>Resource</div>
                                <div style={{ color: 'var(--text-primary)' }}>{entry.resource_type || '-'}{entry.resource_id ? ` #${entry.resource_id}` : ''}</div>
                              </div>
                            </div>
                            {entry.details && Object.keys(entry.details).length > 0 && (
                              <div style={{ marginTop: '10px' }}>
                                <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '4px' }}>Details</div>
                                <pre style={{ background: 'var(--hover-bg)', padding: '8px 12px', borderRadius: '6px', fontSize: '11px', color: 'var(--text-muted)', overflow: 'auto', maxHeight: '120px', margin: 0 }}>
                                  {JSON.stringify(entry.details, null, 2)}
                                </pre>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="pagination">
                <div className="pagination-info">
                  Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, total)} of {total} entries
                </div>
                <div className="pagination-buttons">
                  <button className="pagination-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  <span style={{ fontSize: '13px', color: 'var(--text-muted)', padding: '0 8px' }}>Page {page} of {totalPages}</span>
                  <button className="pagination-btn" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ marginTop: '24px', padding: '12px 16px', background: 'var(--hover-bg)', borderRadius: '8px', border: '1px solid var(--card-border)', fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center' }}>
        Audit records are append-only and retained for 2+ years per compliance policy.
      </div>
    </div>
  );
};

export default AuditPage;
